import {
    AppstoreAddOutlined,
    DeleteOutlined,
    DownOutlined,
    EditOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import { ApolloError, useApolloClient } from '@apollo/client';
import { Button, Dropdown, Menu, message, Modal, Space } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as permissionKind from '../../../../shared/permissions';
import { type RouterSpecsFragment } from '../../../api/fragments/RouterSpecs';
import {
    DeleteRouterDocument,
    type DeleteRouterMutation,
    type DeleteRouterMutationVariables,
} from '../../../api/mutations/deleteRouter';
import hasPermissions from '../../../utilities/hasPermissions';
import { useRouterMenuDrawer } from './menuItems/RouterMenuDrawer';
import { useUpdateDrawer } from './RouterUpdateDrawer';

export type RouterActionsProps = {
    router: RouterSpecsFragment;
    activeTab: string;
};

const RouterActions = ({ router, activeTab }: RouterActionsProps) => {
    const { t } = useTranslation(['routerDetails']);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();
    const routerMenuDrawer = useRouterMenuDrawer(router.menuItems);
    const routerId = router.id;

    const deleteAction = useCallback(() => {
        Modal.confirm({
            title: t('routerDetails:deleteModal.title'),
            icon: <ExclamationCircleOutlined />,
            content: t('routerDetails:deleteModal.content'),
            okText: t('routerDetails:deleteModal.submitButton'),
            okType: 'danger',
            cancelText: t('routerDetails:deleteModal.cancelButton'),
            onOk: async () => {
                try {
                    message.loading({
                        content: t('routerDetails:deleteModal.messages.submitting'),
                        key: 'primary',
                        duration: 0,
                    });

                    await apolloClient
                        .mutate<DeleteRouterMutation, DeleteRouterMutationVariables>({
                            mutation: DeleteRouterDocument,
                            variables: { routerId },
                        })
                        .finally(() => {
                            message.destroy('primary');
                        });

                    message.success({
                        content: t('routerDetails:deleteModal.messages.success'),
                        key: 'primary',
                    });

                    // go back to router list
                    navigate('/admin/system/routers');
                } catch (error) {
                    if (error instanceof ApolloError) {
                        message.error(error.graphQLErrors[0].message);
                    }
                }
            },
        });
    }, [apolloClient, routerId, t, navigate]);

    const updateDrawer = useUpdateDrawer();

    const items = [
        hasPermissions(router.permissions, [permissionKind.updateRouter]) && {
            label: t('routerDetails:mainActionButtons.updateSpecs'),
            key: 'update',
            icon: <EditOutlined />,
            onClick: () => setTimeout(() => updateDrawer.open()),
        },
        hasPermissions(router.permissions, [permissionKind.updateRouter]) && {
            label: t('routerDetails:mainActionButtons.addEndpoint'),
            key: 'addEndpoint',
            icon: <AppstoreAddOutlined />,
            onClick: () => navigate(`/admin/system/routers/${routerId}/addEndpoint`),
        },
        hasPermissions(router.permissions, [permissionKind.deleteRouter]) && {
            label: t('routerDetails:mainActionButtons.delete'),
            key: 'delete',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: deleteAction,
        },
        activeTab === 'menu' && {
            label: t('routerDetails:mainActionButtons.addMenu'),
            key: 'addMenu',
            icon: <EditOutlined />,
            onClick: () => routerMenuDrawer.open(),
        },
    ].filter(Boolean);

    if (!items.length) {
        return null;
    }

    return (
        <>
            <Dropdown overlay={<Menu items={items} />}>
                <Button type="primary">
                    <Space>
                        <span>{t('routerDetails:mainActionButtons.actions')}</span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
            {updateDrawer.render(router)}
            {routerMenuDrawer.render(router)}
        </>
    );
};

export default RouterActions;
